import type { Conversation, PaginationMeta } from '@fluenceur/types';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import { conversationQueryKeys, type ConversationListFilters } from './conversation-query-keys';

interface ConversationsResponse {
  data: Conversation[];
  meta: PaginationMeta;
}

interface ConversationsQueryFilters extends ConversationListFilters {
  enabled?: boolean;
}

export const DEFAULT_CONVERSATIONS_LIMIT = 20;

const fetchConversations = async (
  filters: Omit<ConversationsQueryFilters, 'enabled'>,
  page: number
): Promise<ConversationsResponse> => {
  const response = await api.get<ConversationsResponse>('/dashboard/conversations', {
    params: {
      page,
      limit: filters.limit ?? DEFAULT_CONVERSATIONS_LIMIT,
      search: filters.search,
      status: filters.status,
    },
  });
  return response.data;
};

export const useConversationsQuery = (
  brandId: string | null | undefined,
  filters: ConversationsQueryFilters = {}
) => {
  const { enabled = true, ...queryFilters } = filters;
  const limit = queryFilters.limit ?? DEFAULT_CONVERSATIONS_LIMIT;

  return useInfiniteQuery<
    ConversationsResponse,
    Error,
    InfiniteData<ConversationsResponse, number>,
    ReturnType<typeof conversationQueryKeys.list>,
    number
  >({
    queryKey: conversationQueryKeys.list(brandId ?? '', { ...queryFilters, limit }),
    queryFn: ({ pageParam }) => fetchConversations({ ...queryFilters, limit }, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { currentPage, lastPage: lastPageNumber } = lastPage.meta;
      return currentPage < lastPageNumber ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: true,
    enabled: !!brandId && enabled,
  });
};

export const getConversationsFromPages = (
  pages: ConversationsResponse[] | undefined
): Conversation[] => {
  if (!pages?.length) return [];

  const conversationsById = new Map<string, Conversation>();
  pages.forEach((page) => {
    page.data.forEach((conversation) => {
      conversationsById.set(conversation.id, conversation);
    });
  });

  return Array.from(conversationsById.values());
};
